/**
 * On-Site Area Report configuration — the draft the client edits, and the rule
 * that turns it into the wire request.
 *
 * Ported from the redesigned legacy step (`MeasurementStepRedesign`, ISSUE-2187 /
 * epic ISSUE-1595), NOT from the original one. The redesign changed both the
 * questions asked and how the price is reached: since ISSUE-2188 no measured-extra
 * products are attached to the cart at all — the backend engine derives them from
 * this configuration, so every field here is a price input.
 */

import type { MeasurementOnSiteRequestDTO } from 'models/purchaseFlow/measurementDTO'
import {
  AtticScope,
  CellarScope,
  MeasurementOnSiteAdditionalInformationType,
  MeasurementOnSitePropertyType,
  isAtticScopeMeasured,
  isCellarScopeMeasured
} from 'models/purchaseFlow/measurementDTO'

/**
 * What the form holds. Deliberately NOT the wire shape: unit-based property types
 * are edited as a list of per-unit floor counts, which the wire flattens into two
 * totals.
 */
export interface MeasurementDraftDTO {
  propertyType: MeasurementOnSitePropertyType
  /** Total floor area in m². `null` while the field is empty — 0 is a real answer. */
  surfaceArea: number | null
  /** Floors, for the property types that have no residential units. */
  numberOfFloors: number
  /** One entry per residential unit, holding that unit's floor count. */
  residentialUnits: number[]
  cellarScope: CellarScope
  atticScope: AtticScope
  expressService: boolean
  additionalHouseViews: boolean
  additionalInformation: MeasurementOnSiteAdditionalInformationType[]
  comment: string
}

/** Property types edited as a list of residential units rather than a floor count. */
export const UNIT_BASED_PROPERTY_TYPES: ReadonlySet<MeasurementOnSitePropertyType> = new Set([
  MeasurementOnSitePropertyType.SEMI_DETACHED_HOUSE,
  MeasurementOnSitePropertyType.APARTMENT_BUILDING,
])

export const isUnitBasedPropertyType = (type: MeasurementOnSitePropertyType): boolean =>
  UNIT_BASED_PROPERTY_TYPES.has(type)

/** Fewest units a unit-based type may be ordered with. */
export const MINIMUM_UNITS: ReadonlyMap<MeasurementOnSitePropertyType, number> = new Map([
  [MeasurementOnSitePropertyType.SEMI_DETACHED_HOUSE, 2],
  [MeasurementOnSitePropertyType.APARTMENT_BUILDING, 3],
])

export const minimumUnits = (type: MeasurementOnSitePropertyType): number => MINIMUM_UNITS.get(type) ?? 1

/**
 * Floors a non-unit type starts on. The house starts at 2 because its rate card is
 * written per floor with the first two included; starting at 1 quotes the flat
 * category's price and reads as a bug.
 */
export const DEFAULT_FLOORS: ReadonlyMap<MeasurementOnSitePropertyType, number> = new Map([
  [MeasurementOnSitePropertyType.SINGLE_FAMILY_HOUSE, 2],
])

export const defaultFloors = (type: MeasurementOnSitePropertyType): number => DEFAULT_FLOORS.get(type) ?? 1

export const MEASUREMENT_DEFAULT: MeasurementDraftDTO = {
  propertyType: MeasurementOnSitePropertyType.APARTMENT_SINGLE_FLOORS,
  surfaceArea: null,
  numberOfFloors: defaultFloors(MeasurementOnSitePropertyType.APARTMENT_SINGLE_FLOORS),
  residentialUnits: [],
  cellarScope: CellarScope.NONE,
  atticScope: AtticScope.NONE,
  expressService: false,
  additionalHouseViews: false,
  additionalInformation: [],
  comment: '',
}

/** The draft reset for a property type, keeping the answers that still apply. */
export const forPropertyType = (
  draft: MeasurementDraftDTO,
  propertyType: MeasurementOnSitePropertyType
): MeasurementDraftDTO => ({
  ...draft,
  propertyType,
  numberOfFloors: defaultFloors(propertyType),
  residentialUnits: isUnitBasedPropertyType(propertyType)
    ? Array.from({ length: minimumUnits(propertyType) }, () => 1)
    : [],
})

/**
 * The draft as the backend wants it.
 *
 * Three rules come from the redesign and are the reason this is not a pass-through:
 *  - `numberOfRooms` is never sent; the engine does not price on rooms.
 *  - a unit-based type reports the SUM of its units' floors and the unit COUNT,
 *    because the wire has no per-unit shape.
 *  - the deprecated `basementAreaToMeasure` / `atticAreaToMeasure` booleans must
 *    agree with the scopes, so they are derived rather than stored.
 */
export const toMeasurementRequest = (draft: MeasurementDraftDTO): MeasurementOnSiteRequestDTO => {
  const unitBased = isUnitBasedPropertyType(draft.propertyType)

  return {
    propertyType: draft.propertyType,
    surfaceArea: draft.surfaceArea ?? 0,
    numberOfFloors: unitBased
      ? draft.residentialUnits.reduce((sum, floors) => sum + floors, 0)
      : draft.numberOfFloors,
    ...(unitBased ? { numberOfResidentialUnits: draft.residentialUnits.length } : {}),
    basementAreaToMeasure: isCellarScopeMeasured(draft.cellarScope),
    atticAreaToMeasure: isAtticScopeMeasured(draft.atticScope),
    cellarScope: draft.cellarScope,
    atticScope: draft.atticScope,
    expressService: draft.expressService,
    additionalHouseViews: draft.additionalHouseViews,
    additionalInformation: draft.additionalInformation,
    comment: draft.comment,
  }
}

/**
 * A configuration is complete once it has an area to price. Floors and units always
 * carry a value, and every other answer is optional.
 */
export const measurementIsComplete = (draft: MeasurementDraftDTO | undefined): boolean =>
  !!draft && draft.surfaceArea !== null && draft.surfaceArea > 0
